import { delay } from "@formant/core/common/delay";
import { defined } from "@formant/core/common/defined";
import { logger } from "@formant/core/common/logger";
import { IDeviceDetails } from "@formant/core/model/IDeviceDetails";
import {
    GetPeersRequest,
    GetSessionsRequest,
    Peer,
    RtcSession,
    SignalingPromiseClient,
} from "@formant/protos";

import { IAuthStore } from "./IAuthStore";
import { IDeviceDetailsStore } from "./IDeviceDetailsStore";
import { ISignalingStore } from "./ISignalingStore";
import { StoreCache } from "./StoreCache";

export class SignalingStore implements ISignalingStore {
    private peerCache = new StoreCache<void, Peer[]>();
    private sessionCache = new StoreCache<void, RtcSession[]>({
        staleIntervalMs: 500,
    });
    constructor(
        private signalingClient: SignalingPromiseClient,
        private authStore: IAuthStore,
        private deviceDetailsStore: IDeviceDetailsStore,
    ) {}

    peers(): Peer[] | undefined {
        return this.peerCache.get(undefined, () => this.fetchPeers());
    }

    peerDevices(): IDeviceDetails[] | undefined {
        const peers = this.peers();
        const devices = this.deviceDetailsStore.list({
            enabled: true,
            type: "default",
        });
        if (!peers || !devices) {
            return undefined;
        }

        const peerDeviceIds = new Set(
            peers
                .filter(_ => _.getDeviceId()?.getValue())
                .map(_ => defined(_.getDeviceId()?.getValue())),
        );

        return devices.filter(_ => peerDeviceIds.has(defined(_.id)));
    }

    userPeersInSessionWithDevice(device: IDeviceDetails): Peer[] | undefined {
        const peers = this.peers();
        if (!peers) {
            return;
        }

        const { id } = device;

        const devicePeer = peers.find(_ => _.getDeviceId()?.getValue() === id);
        if (!devicePeer) {
            return;
        }

        const deviceSessions = devicePeer.getSessionIdsList();
        const userPeers = [];
        for (const peer of peers) {
            if (!peer.getUserId()) {
                continue;
            }
            if (
                deviceSessions.some((_: any) =>
                    peer.getSessionIdsList().includes(_),
                )
            ) {
                userPeers.push(peer);
            }
        }
        return userPeers;
    }

    async userPeersInTeleopSessionWithDevice(
        device: IDeviceDetails,
        retryCount = 3,
    ): Promise<Peer[] | undefined> {
        const sessions = this.sessions()
            ?.map(_ => _.toObject())
            .filter(_ => _.sessionType === "teleop");
        if (!sessions || (sessions.length === 0 && retryCount > 0)) {
            await delay(100);
            return this.userPeersInTeleopSessionWithDevice(
                device,
                retryCount - 1,
            );
        }

        const peersInSessionWithDevice =
            this.userPeersInSessionWithDevice(device);

        if (!sessions) {
            return;
        }
        if (!peersInSessionWithDevice) {
            return;
        }
        const sessionsSet = new Set(sessions.map(_ => _.sessionId));

        const peers: Peer[] = [];

        // check if any of the current sessions belong to any of the peers in
        // a session with the device
        for (const peer of peersInSessionWithDevice) {
            for (const sessionId of peer.getSessionIdsList()) {
                if (sessionsSet.has(sessionId)) {
                    peers.push(peer);
                }
            }
        }
        return peers;
    }

    async fetchPeers(): Promise<Peer[]> {
        const response = await (async () => {
            try {
                return await this.signalingClient.getPeers(
                    new GetPeersRequest(),
                    {
                        authorization: await this.authStore.token(),
                    },
                );
            } catch (error) {
                logger.warn("Error when getting peers", { error });
            }
        })();

        if (!response) {
            return [];
        }

        const peers = response.getPeersList();
        if (!peers) {
            return [];
        }

        this.peerCache.set(undefined, peers);
        return peers;
    }

    async fetchSessions(): Promise<RtcSession[]> {
        const response = await (async () => {
            try {
                return await this.signalingClient.getSessions(
                    new GetSessionsRequest(),
                    {
                        authorization: await this.authStore.token(),
                    },
                );
            } catch (error) {
                logger.warn("Error when getting sessions", { error });
            }
        })();

        if (!response) {
            return [];
        }

        return response.getSessionsList();
    }

    sessions(): RtcSession[] | undefined {
        return this.sessionCache.get(undefined, () => this.fetchSessions());
    }
}
